import { useState, useEffect, useCallback } from 'react';
import { 
  requestNotificationPermission, 
  isNotificationSupported, 
  getNotificationPermission 
} from '../lib/notify';

export interface UseNotificationPermissionReturn {
  permission: NotificationPermission;
  isSupported: boolean;
  isRequesting: boolean;
  requestPermission: () => Promise<NotificationPermission>;
  hasPermission: boolean;
}

/**
 * Hook to manage notification permissions
 */
export const useNotificationPermission = (): UseNotificationPermissionReturn => {
  const [permission, setPermission] = useState<NotificationPermission>('default');
  const [isRequesting, setIsRequesting] = useState(false);
  const isSupported = isNotificationSupported();

  // Update permission state
  useEffect(() => {
    if (isSupported) {
      setPermission(getNotificationPermission());
    }
  }, [isSupported]);

  // Listen for permission changes (if supported)
  useEffect(() => {
    if (!isSupported || !('permissions' in navigator)) return;

    const checkPermission = async () => {
      try {
        const result = await navigator.permissions.query({ name: 'notifications' as PermissionName });
        setPermission(result.state as NotificationPermission);
        
        result.addEventListener('change', () => {
          setPermission(result.state as NotificationPermission);
        });
      } catch (error) {
        // Fallback to checking Notification.permission
        setPermission(getNotificationPermission());
      }
    };

    checkPermission();
  }, [isSupported]);

  const handleRequestPermission = useCallback(async (): Promise<NotificationPermission> => {
    if (!isSupported) {
      return 'denied';
    }

    setIsRequesting(true);
    try {
      const newPermission = await requestNotificationPermission();
      setPermission(newPermission);
      return newPermission;
    } catch (error) {
      console.error('Error requesting notification permission:', error);
      return 'denied';
    } finally {
      setIsRequesting(false);
    }
  }, [isSupported]);

  return {
    permission,
    isSupported,
    isRequesting,
    requestPermission: handleRequestPermission,
    hasPermission: permission === 'granted'
  };
};

export default useNotificationPermission; 